import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { taxOffices, type TaxOffice } from '@/api/taxOffices';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { useToast } from '@/components/ToastProvider';

/**
 * Довідник територіальних органів ДПС (ДПІ): код → назва → область.
 *
 * Юзер на картці організації обирає свою ДПІ зі списку — код і назва звідси
 * підставляються у звіти/запити до ДПС. Структура ДПС змінюється (з 01.01.2025
 * нові коди) — тому є оновлення: вставка рядків «код; назва; область»
 * (get-or-create по коду). Оновлення потребує права admin.settings.manage.
 */
export default function TaxOfficesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [showUpdate, setShowUpdate] = useState(false);
  const [pasteText, setPasteText] = useState('');

  const listQ = useQuery({
    queryKey: ['tax-offices-page', q],
    queryFn: () => taxOffices.list({ q: q || undefined, limit: 500 }),
  });

  const upsert = useMutation({
    mutationFn: (items: { code: string; name: string; region_name?: string | null }[]) =>
      taxOffices.bulkUpsert(items),
    onSuccess: (r) => {
      toast.show({ type: 'success', title: 'Довідник ДПІ оновлено',
        message: `Додано ${r.created}, змінено ${r.updated} (всього ${r.total}).` });
      setShowUpdate(false);
      setPasteText('');
      qc.invalidateQueries({ queryKey: ['tax-offices-page'] });
      qc.invalidateQueries({ queryKey: ['tax-offices'] });
    },
    onError: () => toast.show({ type: 'error', title: 'Помилка',
      message: 'Не вдалося оновити довідник (потрібне право адміністратора).' }),
  });

  const parsed = useMemo(() => {
    // Рядки «код; назва; область» (роздільник ; або TAB). Область — опційно.
    return pasteText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((l) => {
        const parts = l.split(/[\t;]/).map((p) => p.trim());
        return { code: parts[0] ?? '', name: parts[1] ?? '', region_name: parts[2] || null };
      })
      .filter((it) => it.code && it.name);
  }, [pasteText]);

  const columns: ColumnDef<TaxOffice>[] = [
    { key: 'code', header: 'Код ДПІ', width: 110,
      cell: (r) => <b>{r.code}</b>, sortValue: (r) => r.code },
    { key: 'name', header: 'Назва', cell: (r) => r.name, sortValue: (r) => r.name },
    { key: 'region', header: 'Область', width: 220,
      cell: (r) => r.region_name ?? '', sortValue: (r) => r.region_name ?? '' },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Органи ДПС (ДПІ)</h2>
        <input value={q} placeholder="пошук за кодом, назвою або областю"
          onChange={(e) => setQ(e.target.value)} style={{ minWidth: 280 }} />
        <span style={{ flex: 1 }} />
        <button onClick={() => setShowUpdate((v) => !v)}>
          {showUpdate ? 'сховати оновлення' : 'оновити довідник'}
        </button>
      </div>

      <small style={{ color: 'var(--muted)', display: 'block', marginBottom: '0.5rem' }}>
        Код і назва вашої ДПІ обираються на картці організації (вкладка «Коди») і
        підставляються у звіти та запити до ДПС автоматично. Довідник актуальний
        станом на 2025 рік — оновлюйте, коли ДПС змінює структуру.
      </small>

      {showUpdate && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem',
          marginBottom: '0.75rem', background: '#f8fafc' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>
            Оновлення довідника ДПІ
          </div>
          <div style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
            Вставте рядки у форматі <code>код; назва; область</code> (по одному в рядку;
            роздільник «;» або табуляція). Наявні коди оновлюються, нові — додаються.
            Наприклад: <code>1305; Львівська ДПІ; Львівська область</code>
          </div>
          <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)}
            rows={7} style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.85rem' }}
            placeholder={'1305; Львівська ДПІ; Львівська область\n2657; Правобережна ДПІ (Оболонський р-н); м. Київ'} />
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
            marginTop: '0.5rem' }}>
            <button className="primary" disabled={!parsed.length || upsert.isPending}
              onClick={() => upsert.mutate(parsed)}>
              застосувати ({parsed.length})
            </button>
            <span style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
              Розпізнано рядків: {parsed.length}
            </span>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={listQ.data?.items ?? []}
        rowKey={(r) => r.code}
        loading={listQ.isLoading}
        emptyText="Немає ДПІ"
        initialSortKey="code"
      />
      <small style={{ color: 'var(--muted)' }}>
        Усього ДПІ: {listQ.data?.total ?? 0}
      </small>
    </div>
  );
}
